import { useEffect, useState } from "react";
import { Download, Play, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Card, Field, PageHeader } from "../../components/common/UI";
import { ReportCharts, ReportTable } from "../../components/reports/ReportBlocks";
import { downloadBlob } from "../../components/reports/useReportMeta";

const EMPTY = { dataset: "measures", columns: [], group_by: [], aggregation: "count", metric_field: "", chart_type: "bar", filters: [], limit: 500 };

function SavedList({ items, onLoad, onDelete }) {
  return (
    <Card title="Saved custom reports" testId="custom-saved-list">
      {!items.length && <p className="text-xs text-slate-400">No saved custom reports yet.</p>}
      <ul className="space-y-1.5">
        {items.map((s) => (
          <li key={s.id} className="flex items-center justify-between gap-2 text-xs">
            <button className="text-left text-sky-700 hover:underline" onClick={() => onLoad(s)} data-testid={`custom-load-${s.id}`}>{s.name}</button>
            {s.is_owner && <button onClick={() => onDelete(s)} className="text-slate-400 hover:text-red-600" data-testid={`custom-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function FilterRows({ cfg, setCfg, fields }) {
  const upd = (i, k, v) => setCfg({ ...cfg, filters: cfg.filters.map((f, j) => (j === i ? { ...f, [k]: v } : f)) });
  return (
    <div className="space-y-2">
      {cfg.filters.map((f, i) => (
        <div key={i} className="flex gap-2">
          <select className="otms-input h-8 text-xs" value={f.field} onChange={(e) => upd(i, "field", e.target.value)} data-testid={`custom-filter-field-${i}`}>
            {fields.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
          </select>
          <select className="otms-input h-8 w-28 text-xs" value={f.op} onChange={(e) => upd(i, "op", e.target.value)}>
            {[["eq", "equals"], ["contains", "contains"], ["gte", "≥"], ["lte", "≤"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input className="otms-input h-8 text-xs" value={f.value} onChange={(e) => upd(i, "value", e.target.value)} data-testid={`custom-filter-value-${i}`} />
          <Button size="sm" variant="ghost" onClick={() => setCfg({ ...cfg, filters: cfg.filters.filter((_, j) => j !== i) })}>×</Button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={() => setCfg({ ...cfg, filters: [...cfg.filters, { field: fields[0].key, op: "eq", value: "" }] })} data-testid="custom-add-filter-btn">Add filter</Button>
    </div>
  );
}

export default function CustomReports() {
  const { can } = useAuth();
  const [datasets, setDatasets] = useState([]);
  const [cfg, setCfg] = useState(EMPTY);
  const [period, setPeriod] = useState({ date_from: "", date_to: "" });
  const [name, setName] = useState("Custom report");
  const [rep, setRep] = useState(null);
  const [saved, setSaved] = useState([]);
  const loadSaved = () => api.get("/reports/saved", { params: { kind: "custom" } }).then((r) => setSaved(r.data.items));
  useEffect(() => { api.get("/reports/custom/datasets").then((r) => setDatasets(r.data.datasets)); loadSaved(); }, []);
  const ds = datasets.find((d) => d.key === cfg.dataset);
  const fields = ds?.fields || [];
  const metric = fields.find((f) => f.key === cfg.metric_field);
  const aggs = metric ? metric.aggregations : ["count", "count_distinct"];
  const body = () => ({ config: { ...cfg, metric_field: cfg.metric_field || null }, filters: Object.fromEntries(Object.entries(period).filter(([, v]) => v)), name });
  const run = async () => {
    try { setRep((await api.post("/reports/custom/run", body())).data); } catch (e) { toast.error(errMsg(e)); }
  };
  const save = async () => {
    try {
      await api.post("/reports/saved", { kind: "custom", name, config: body() });
      toast.success("Custom report saved"); loadSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = (k, list) => setCfg({ ...cfg, [list]: cfg[list].includes(k) ? cfg[list].filter((x) => x !== k) : [...cfg[list], k].slice(0, list === "group_by" ? 2 : 20) });
  const exportAs = (format) => downloadBlob(api.post("/reports/custom/export", body(), { params: { format }, responseType: "blob" }), `custom.${format}`).catch(() => toast.error("Export failed"));
  return (
    <div data-testid="custom-reports-page">
      <PageHeader eyebrow="Reports and Analytics" title="Custom Report Builder" subtitle="Build reports from approved datasets and fields. Raw SQL is not supported; tenant and record permissions always apply." />
      <div className="grid gap-5 lg:grid-cols-[1fr_280px]">
        <Card title="Report configuration" testId="custom-config">
          <div className="grid gap-4 md:grid-cols-3">
            <Field label="Report name"><input className="otms-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="custom-name-input" /></Field>
            <Field label="Dataset">
              <select className="otms-input" value={cfg.dataset} onChange={(e) => setCfg({ ...EMPTY, dataset: e.target.value })} data-testid="custom-dataset-select">
                {datasets.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
              </select>
            </Field>
            <Field label="Chart">
              <select className="otms-input" value={cfg.chart_type} onChange={(e) => setCfg({ ...cfg, chart_type: e.target.value })} data-testid="custom-chart-select">
                {["bar", "line", "pie", "none"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="From"><input type="date" className="otms-input" value={period.date_from} onChange={(e) => setPeriod({ ...period, date_from: e.target.value })} data-testid="custom-date-from" /></Field>
            <Field label="To"><input type="date" className="otms-input" value={period.date_to} onChange={(e) => setPeriod({ ...period, date_to: e.target.value })} data-testid="custom-date-to" /></Field>
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <Field label="Columns (when not grouping)">
              <div className="flex flex-wrap gap-1.5">{fields.map((f) => (
                <button key={f.key} onClick={() => toggle(f.key, "columns")} data-testid={`custom-col-${f.key}`}
                  className={`rounded-full px-2.5 py-0.5 text-[11px] ring-1 ${cfg.columns.includes(f.key) ? "bg-sky-600 text-white ring-sky-600" : "bg-white text-slate-600 ring-slate-200"}`}>{f.label}</button>
              ))}</div>
            </Field>
            <Field label="Group by (up to 2)">
              <div className="flex flex-wrap gap-1.5">{fields.map((f) => (
                <button key={f.key} onClick={() => toggle(f.key, "group_by")} data-testid={`custom-group-${f.key}`}
                  className={`rounded-full px-2.5 py-0.5 text-[11px] ring-1 ${cfg.group_by.includes(f.key) ? "bg-slate-900 text-white ring-slate-900" : "bg-white text-slate-600 ring-slate-200"}`}>{f.label}</button>
              ))}</div>
            </Field>
          </div>
          {cfg.group_by.length > 0 && (
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <Field label="Measure field (optional)">
                <select className="otms-input" value={cfg.metric_field} onChange={(e) => setCfg({ ...cfg, metric_field: e.target.value, aggregation: "count" })} data-testid="custom-metric-select">
                  <option value="">Records</option>
                  {fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>
              </Field>
              <Field label="Aggregation">
                <select className="otms-input" value={cfg.aggregation} onChange={(e) => setCfg({ ...cfg, aggregation: e.target.value })} data-testid="custom-agg-select">
                  {aggs.map((a) => <option key={a} value={a}>{a.replace("_", " ")}</option>)}
                </select>
              </Field>
            </div>
          )}
          <div className="mt-4"><Field label="Filters">{fields.length > 0 && <FilterRows cfg={cfg} setCfg={setCfg} fields={fields} />}</Field></div>
          <div className="mt-5 flex flex-wrap gap-2">
            <Button size="sm" onClick={run} data-testid="custom-run-btn"><Play className="mr-1 h-3.5 w-3.5" />Run report</Button>
            <Button size="sm" variant="outline" onClick={save} data-testid="custom-save-btn"><Save className="mr-1 h-3.5 w-3.5" />Save</Button>
            {rep && can("measure.export") && ["csv", "xlsx", "pdf"].map((f) => (
              <Button key={f} size="sm" variant="outline" onClick={() => exportAs(f)} data-testid={`custom-export-${f}-btn`}><Download className="mr-1 h-3.5 w-3.5" />{f.toUpperCase()}</Button>
            ))}
          </div>
        </Card>
        <SavedList items={saved} onLoad={(s) => { setCfg({ ...EMPTY, ...s.config.config, metric_field: s.config.config.metric_field || "" }); setName(s.name); setPeriod({ date_from: "", date_to: "", ...s.config.filters }); }}
          onDelete={async (s) => { await api.delete(`/reports/saved/${s.id}`); loadSaved(); }} />
      </div>
      {rep && <div className="mt-6" data-testid="custom-results"><ReportCharts charts={rep.charts} />{rep.tables.map((t) => <ReportTable key={t.id} t={t} />)}</div>}
    </div>
  );
}
